import React from 'react';
import { Download, Share2, Check, Copy } from 'lucide-react';
import type { SportType, WorkoutSegment } from '@/types';
import type { TranslationKeys } from '@/core/i18n/translations';
import { useIsMobile } from '@/shared/hooks/useIsMobile';
import { GarminMark, PolarFlowMark, TrainingPeaksMark } from './ExportBrandMarks';

/** Fixed width for cloud export tiles so captions share one baseline row. */
const EXPORT_SLOT = 'w-[10.75rem]';
const EXPORT_BTN_H = 'h-24';
const ICON_MARK = 'w-6 h-6 shrink-0';
const EXPORT_BTN_BASE = `${EXPORT_BTN_H} w-full rounded-xl font-bold flex flex-col items-center justify-center gap-1.5 px-2 py-2 text-[11px] leading-tight text-center touch-manipulation`;
const CAPTION_ROW = `${EXPORT_SLOT} h-[2.75rem] mt-1.5 flex items-end justify-center text-center text-[10px] sm:text-[11px] leading-snug text-slate-500 dark:text-slate-400 px-0.5`;

export interface WorkoutExportPanelProps {
  t: TranslationKeys;
  sport: SportType;
  segments: WorkoutSegment[];
  isLoading: boolean;
  activePlatform: 'tp' | 'intervals';
  setActivePlatform: (p: 'tp' | 'intervals') => void;
  generatedMarkdown: string;
  copied: boolean;
  tpSending: boolean;
  garminSending: boolean;
  isMobileLayout: boolean;
  canShareText: boolean;
  sendToTrainingPeaks: () => void;
  sendToGarminConnect: () => void;
  /** Polar Flow phased target (browser + extension); same pipeline as calendar export. */
  sendToPolarFlow: () => void | Promise<void>;
  handleDownloadZwo: () => void | Promise<void>;
  handleDownloadFit: () => void | Promise<void>;
  handleShareWorkoutDescription: () => void | Promise<void>;
  copyToClipboard: () => void;
  cloudSending: boolean;
}

export function WorkoutExportPanel({
  t,
  sport,
  segments,
  isLoading,
  activePlatform,
  setActivePlatform,
  generatedMarkdown,
  copied,
  tpSending,
  garminSending,
  isMobileLayout,
  canShareText,
  sendToTrainingPeaks,
  sendToGarminConnect,
  sendToPolarFlow,
  handleDownloadZwo,
  handleDownloadFit,
  handleShareWorkoutDescription,
  copyToClipboard,
  cloudSending,
}: WorkoutExportPanelProps) {
  const hideExtensionExports = useIsMobile();

  if (segments.length === 0 || isLoading) return null;

  return (
    <section className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-700 shadow-xl backdrop-filter backdrop-blur-lg bg-opacity-10 dark:bg-opacity-10 overflow-hidden animate-in fade-in slide-in-from-bottom-4">
      <div className="md:hidden p-4">
        <p className="text-sm text-gray-500 dark:text-slate-400 text-center leading-relaxed">{t.mobileExportDesktopOnlyHint}</p>
      </div>

      <div className="hidden md:block">
        <div className="p-3 border-b dark:border-slate-800 flex gap-4">
          <button
            type="button"
            onClick={() => setActivePlatform('tp')}
            className={`min-h-[44px] pb-2 px-2 border-b-2 touch-manipulation ${
              activePlatform === 'tp' ? 'border-orange-500' : 'border-transparent opacity-50'
            }`}
          >
            {t.platformTP}
          </button>
          <button
            type="button"
            onClick={() => setActivePlatform('intervals')}
            className={`min-h-[44px] pb-2 px-2 border-b-2 touch-manipulation ${
              activePlatform === 'intervals' ? 'border-orange-500' : 'border-transparent opacity-50'
            }`}
          >
            {t.platformIntervals}
          </button>
        </div>
        <div className="p-4">
          {activePlatform === 'tp' ? (
            <div className="flex flex-col items-center gap-4">
              <div className="flex flex-wrap gap-3 md:gap-4 justify-center items-end">
                <div className={`flex flex-col items-stretch ${EXPORT_SLOT}`}>
                  <button
                    type="button"
                    data-testid="export-download-zwo"
                    onClick={() => void handleDownloadZwo()}
                    className={`${EXPORT_BTN_BASE} bg-orange-600 text-white hover:bg-orange-500 transition-all`}
                  >
                    <Download className={`${ICON_MARK}`} aria-hidden />
                    {t.downloadBtn}
                  </button>
                  <p className={CAPTION_ROW}>{t.downloadZwoFormatHint}</p>
                </div>
                {sport === 'bike' && (
                  <div className={`flex flex-col items-stretch ${EXPORT_SLOT} max-w-full sm:max-w-none`}>
                    <button
                      type="button"
                      data-testid="export-download-fit"
                      onClick={() => void handleDownloadFit()}
                      className={`${EXPORT_BTN_BASE} bg-emerald-700 text-white hover:bg-emerald-600 transition-all`}
                    >
                      <Download className={`${ICON_MARK}`} aria-hidden />
                      {t.downloadFitBtn}
                    </button>
                    {isMobileLayout ? (
                      <p className={`${CAPTION_ROW} text-slate-500 dark:text-slate-400`}>{t.mobileGarminFitHint}</p>
                    ) : (
                      <div className={CAPTION_ROW} aria-hidden />
                    )}
                  </div>
                )}
                {isMobileLayout && canShareText ? (
                  <div className={`flex flex-col items-stretch ${EXPORT_SLOT}`}>
                    <button
                      type="button"
                      data-testid="share-workout-description"
                      onClick={() => void handleShareWorkoutDescription()}
                      className={`${EXPORT_BTN_BASE} border-2 border-slate-400 dark:border-slate-500 text-slate-800 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-800 transition-all`}
                    >
                      <Share2 className={`${ICON_MARK}`} aria-hidden />
                      {t.shareWorkoutDescriptionBtn}
                    </button>
                    <div className={CAPTION_ROW} aria-hidden />
                  </div>
                ) : null}
                {!hideExtensionExports ? (
                  <>
                    <div className={`flex flex-col items-stretch ${EXPORT_SLOT}`}>
                      <button
                        type="button"
                        data-testid="export-training-peaks"
                        onClick={sendToTrainingPeaks}
                        disabled={tpSending}
                        className={`${EXPORT_BTN_BASE} bg-blue-600 text-white hover:bg-blue-500 transition-all disabled:opacity-60`}
                      >
                        <TrainingPeaksMark className={ICON_MARK} />
                        {tpSending ? '...' : t.sendToTPBtn}
                      </button>
                      <p className={CAPTION_ROW}>{t.exportCaptionTrainingPeaks}</p>
                    </div>
                    {(sport === 'bike' || sport === 'run') && (
                      <div className={`flex flex-col items-stretch ${EXPORT_SLOT}`}>
                        <button
                          type="button"
                          data-testid="export-garmin"
                          onClick={sendToGarminConnect}
                          disabled={garminSending}
                          className={`${EXPORT_BTN_BASE} bg-sky-700 text-white hover:bg-sky-600 transition-all disabled:opacity-60`}
                        >
                          <GarminMark className={ICON_MARK} />
                          {garminSending ? '...' : t.sendToGarminBtn}
                        </button>
                        <p className={CAPTION_ROW}>{t.exportCaptionGarminConnect}</p>
                      </div>
                    )}
                    <div className={`flex flex-col items-stretch ${EXPORT_SLOT}`}>
                      <button
                        type="button"
                        data-testid="export-polar-flow"
                        onClick={() => void sendToPolarFlow()}
                        disabled={cloudSending}
                        className={`${EXPORT_BTN_BASE} bg-indigo-600 text-white hover:bg-indigo-500 transition-all disabled:opacity-60`}
                      >
                        <PolarFlowMark className={ICON_MARK} />
                        {cloudSending ? '...' : t.sendToPolarFlowBtn}
                      </button>
                      <p className={CAPTION_ROW}>{t.exportCaptionPolarFlow}</p>
                    </div>
                  </>
                ) : null}
              </div>
              <p className="text-[11px] text-slate-500 dark:text-slate-400 max-w-2xl text-center leading-relaxed">
                {t.exportManualFileHint}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex flex-wrap justify-between items-center gap-2">
                <span className="text-xs text-slate-500">{t.intervalsInstruction}</span>
                <button
                  type="button"
                  onClick={copyToClipboard}
                  className="text-xs bg-slate-100 dark:bg-slate-800 min-h-[44px] px-3 rounded-lg inline-flex items-center gap-2 touch-manipulation"
                >
                  {copied ? <Check className="w-4 h-4 shrink-0" aria-hidden /> : <Copy className="w-4 h-4 shrink-0" aria-hidden />}
                  {copied ? t.copied : t.copyText}
                </button>
              </div>
              <textarea
                readOnly
                value={generatedMarkdown}
                className="w-full h-48 bg-slate-50 dark:bg-slate-950 border dark:border-slate-800 rounded p-4 font-mono text-sm focus:outline-none"
              />
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
